class CartRemoveButton extends HTMLElement {
  constructor() {
    super()

    this.addEventListener('click', event => {
      event.preventDefault()
      const cartItems =
        this.closest('cart-items') || this.closest('cart-drawer-items')
      const hasGiftBox =
        this.closest('.cart-item').dataset.productHasGiftbox === 'true'
      const hasMonogramProduct =
        this.closest('.cart-item').dataset.isPersonalised === 'true'

      if (hasGiftBox || hasMonogramProduct) {
        const groupId = this.dataset.groupId
        cartItems.removeGroupItems(groupId)
      } else {
        cartItems.updateQuantity(this.dataset.index, 0)
      }
    })
  }
}

customElements.define('cart-remove-button', CartRemoveButton)

class CartItems extends HTMLElement {
  constructor() {
    super()
    this.lineItemStatusElement =
      document.getElementById('shopping-cart-line-item-status') ||
      document.getElementById('CartDrawer-LineItemStatus')

    const debouncedOnChange = debounce(event => {
      this.onChange(event)
    }, ON_CHANGE_DEBOUNCE_TIMER)

    this.addEventListener('change', debouncedOnChange.bind(this))
    // Handle click on "Remove gift box" button in the gift footer
    this.addEventListener('click', event => {
      const removeGiftBtn = event.target.closest(
        '.cart-item__gift_box-remove-button-option'
      )
      if (!removeGiftBtn) return
      event.preventDefault()
      event.stopPropagation()

      // Get groupId from the button's data attribute, or find the parent cart-item row
      // The button is in an addons row, so we need to traverse to find the main cart-item row
      let groupId = removeGiftBtn.dataset?.groupId
      if (!groupId) {
        const addonsRow = removeGiftBtn.closest('tr.cart-item__addons-row')
        const parentRow =
          addonsRow?.previousElementSibling?.classList?.contains('cart-item')
            ? addonsRow.previousElementSibling
            : removeGiftBtn.closest('.cart-item')
        groupId = parentRow?.dataset?.groupId
      }
      if (!groupId) return

      this.removeGiftBoxAndNote(groupId)
    })

    // Handle click on "Edit personalisation" button
    this.addEventListener('click', async event => {
      const editPersonalisationBtn = event.target.closest(
        '.cart-item__personalisation-edit-button-option'
      )
      if (!editPersonalisationBtn) return
      event.preventDefault()
      event.stopPropagation()

      // The edit button is in an addons-row, which is a sibling of the cart-item row
      // First find the parent row - we need it later for item key and line number
      let parentRow = null
      const addonsRow = editPersonalisationBtn.closest('tr.cart-item__addons-row')
      if (addonsRow) {
        let prevSibling = addonsRow.previousElementSibling
        while (prevSibling && !prevSibling.classList.contains('cart-item')) {
          prevSibling = prevSibling.previousElementSibling
        }
        parentRow = prevSibling
      }
      if (!parentRow) {
        parentRow = editPersonalisationBtn.closest('.cart-item')
      }
      
      let groupId = editPersonalisationBtn.dataset?.groupId || parentRow?.dataset?.groupId
      const variantId = editPersonalisationBtn.getAttribute('data-variant-id')

      if (!groupId) {
        GiftUtils.log('No groupId found for edit')
        return
      }

      // Get cart item data to populate drawer
      try {
        const cart = await GiftUtils.fetchCart()

        // Find the main item with this groupId
        const mainItem = cart.items.find(
          item =>
            String(item.properties?._groupId) === String(groupId) &&
            !item.properties?._addon
        )

        if (!mainItem) {
          GiftUtils.log('Main item not found for edit')
          return
        }

        // Property names come from the locale via window.personalisationStrings
        const initialsProperty = window.personalisationStrings?.initialsProperty
        const fontProperty = window.personalisationStrings?.fontProperty
        const colorProperty = window.personalisationStrings?.colorProperty

        if (!initialsProperty || !fontProperty || !colorProperty) {
          GiftUtils.log('Missing personalisationStrings — cannot open edit drawer')
          return
        }

        // Get existing values
        const existingInitials = mainItem.properties?.[initialsProperty] || ''
        const existingFont = mainItem.properties?.[fontProperty] || ''
        const existingColor = mainItem.properties?.[colorProperty] || ''

        // Get item key and line number
        const itemKeyElement = parentRow.querySelector('[data-itemkeyid]')
        const itemKey = itemKeyElement?.getAttribute('data-itemkeyid')
        const itemLineOnCart = parentRow
          .querySelector('[data-itemlineoncart]')
          ?.getAttribute('data-itemlineoncart')
        const rowIndex = parentRow.getAttribute('data-index')
        const rowId = parentRow.getAttribute('id')

        let lineNumber = null
        if (itemLineOnCart) {
          lineNumber = parseInt(itemLineOnCart)
        } else if (rowIndex) {
          lineNumber = parseInt(rowIndex)
        } else if (rowId && rowId.includes('CartDrawer-Item-')) {
          lineNumber = parseInt(rowId.replace('CartDrawer-Item-', ''))
        }

        // Open drawer and populate with existing values
        const personalisationDrawer = document.querySelector(
          'personalisation-drawer'
        )
        if (personalisationDrawer) {
          // Set cart item context
          personalisationDrawer.cartItemContext = {
            line: lineNumber,
            itemKey: itemKey,
            variantId: variantId,
          }

          // Populate drawer with existing values
          const initialsInput = personalisationDrawer.querySelector(
            '#PersonalisationInitials'
          )
          const fontRadios = personalisationDrawer.querySelectorAll(
            '.personalisation-drawer__font-radio'
          )
          const colorRadios = personalisationDrawer.querySelectorAll(
            '.personalisation-drawer__color-radio'
          )
          const termsCheckbox = personalisationDrawer.querySelector(
            '#AcceptsPersonalisationTerms'
          )

          if (initialsInput && existingInitials) {
            initialsInput.value = existingInitials
            personalisationDrawer.checkInitialsLength(initialsInput)
          }

          if (fontRadios && existingFont) {
            fontRadios.forEach(radio => {
              if (radio.value === existingFont) {
                radio.checked = true
                radio.dispatchEvent(new Event('change'))
              }
            })
          }

          if (colorRadios && existingColor) {
            colorRadios.forEach(radio => {
              if (radio.value === existingColor) {
                radio.checked = true
                radio.dispatchEvent(new Event('change'))
              }
            })
          }

          if (termsCheckbox) {
            termsCheckbox.checked = true
          }

          // Open the drawer
          personalisationDrawer.openDrawer()
        }
      } catch (error) {
        console.error(
          '[Personalisation Debug] Error opening drawer for edit:',
          error
        )
      }
    })

    // Handle click on "Remove personalisation" button
    this.addEventListener('click', event => {
      const removePersonalisationBtn = event.target.closest(
        '.cart-item__personalisation-remove-button-option'
      )
      if (!removePersonalisationBtn) return
      event.preventDefault()
      event.stopPropagation()

      // Get groupId from the button's data attribute, or find the parent cart-item row
      // The button is in an addons row, so we need to traverse to find the main cart-item row
      let groupId = removePersonalisationBtn.dataset?.groupId
      if (!groupId) {
        const addonsRow = removePersonalisationBtn.closest(
          'tr.cart-item__addons-row'
        )
        const parentRow =
          addonsRow?.previousElementSibling?.classList?.contains('cart-item')
            ? addonsRow.previousElementSibling
            : removePersonalisationBtn.closest('.cart-item')
        groupId = parentRow?.dataset?.groupId
      }
      if (!groupId) return

      this.removePersonalisation(groupId)
    })

    this.initProgressBar()
  }

  cartUpdateUnsubscriber = undefined
  isUpdating = false

  connectedCallback() {
    this.cartUpdateUnsubscriber = subscribe(
      PUB_SUB_EVENTS.cartUpdate,
      event => {
        if (event.source === 'cart-items') {
          return
        }
        this.onCartUpdate()
      }
    )

    // Refresh cart items when page is loaded from cache (back button from checkout)
    const handlePageShow = event => {
      // event.persisted is true when page is loaded from cache (back button)
      if (event.persisted) {
        // Small delay to ensure DOM is ready
        setTimeout(() => {
          if (!this.isUpdating) {
            this.onCartUpdate()
          }
        }, 150)
      }
    }

    // Refresh cart items when page becomes visible (returning from checkout)
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        // Small delay to ensure page is fully loaded, and debounce multiple calls
        setTimeout(() => {
          if (!this.isUpdating) {
            this.onCartUpdate()
          }
        }, 200)
      }
    }

    window.addEventListener('pageshow', handlePageShow)
    document.addEventListener('visibilitychange', handleVisibilityChange)

    // Store cleanup functions
    this.pageNavigationCleanup = () => {
      window.removeEventListener('pageshow', handlePageShow)
      document.removeEventListener('visibilitychange', handleVisibilityChange)
    }
  }

  /**
   * Remove the gift-box addon line(s) for a given group and clear the Note + gift properties on the main item.
   * Keeps other properties (e.g. personalisation) intact.
   * @param {string|number} groupId
   */
  async removeGiftBoxAndNote(groupId) {
    try {
      this.enableLoading()

      const cart = await GiftUtils.fetchCart()
      GiftUtils.log('[removeGiftBox] Initial cart', {
        items: cart.items.map((it, idx) => ({
          line: idx + 1,
          id: it.id,
          variant_id: it.variant_id,
          properties: it.properties,
        })),
      })

      // 1) Snapshot all group items BEFORE any removals
      const mainItem = cart.items.find(
        item =>
          String(item.properties?._groupId) === String(groupId) &&
          !item.properties?._addon
      )
      if (!mainItem) throw new Error('Main item not found for groupId: ' + groupId)

      // Check for personalisation addon to preserve
      const personalisationAddon = cart.items.find(
        item =>
          String(item.properties?._groupId) === String(groupId) &&
          (item.properties?._addon === 'true' || item.properties?._addon === true) &&
          item.properties?._addonType === 'Personalisation'
      )

      const hasPersonalisation = Boolean(
        personalisationAddon ||
        mainItem.properties?.[window.personalisationStrings?.initialsProperty]
      )

      GiftUtils.log('[removeGiftBox] hasPersonalisation:', hasPersonalisation, 'personalisationAddon:', personalisationAddon)

      // 2) Remove ALL items in this group (descending line order to preserve indices)
      const groupLines = []
      cart.items.forEach((item, idx) => {
        if (String(item.properties?._groupId) === String(groupId)) {
          groupLines.push(idx + 1)
        }
      })
      groupLines.sort((a, b) => b - a)

      GiftUtils.log('[removeGiftBox] Removing all group lines:', groupLines)
      for (const line of groupLines) {
        const resp = await GiftUtils.cartPost('/cart/change.js', { line, quantity: 0 })
        if (!resp.ok) {
          throw new Error(`/cart/change.js failed removing line ${line}: ${resp.status}`)
        }
      }

      // 3) Build cleaned properties (remove gift box props, keep personalisation props)
      const skipKeys = new Set([
        '_hasGiftBox',
        '_giftProductHandle',
        '_giftPid',
        'Note',
      ])

      const cleanedProps = Object.entries(mainItem.properties || {}).reduce(
        (acc, [key, val]) => {
          if (key === '_groupId') {
            if (hasPersonalisation) acc[key] = val
            return acc
          }
          if (!skipKeys.has(key) && val != null && val !== '') {
            acc[key] = val
          }
          return acc
        },
        {}
      )

      GiftUtils.log('[removeGiftBox] cleanedProps:', cleanedProps)

      // 4) Re-add main item with cleaned properties
      const addPayload = {
        id: mainItem.variant_id,
        quantity: mainItem.quantity,
        properties: cleanedProps,
      }
      const sellingPlanId = mainItem.selling_plan_allocation?.selling_plan?.id
      if (sellingPlanId) addPayload.selling_plan = sellingPlanId

      const addResp = await GiftUtils.cartPost('/cart/add.js', addPayload)
      if (!addResp.ok) {
        console.error('[Cart] Re-add main item failed, attempting restore')
        const restorePayload = {
          id: mainItem.variant_id,
          quantity: mainItem.quantity,
          properties: mainItem.properties,
        }
        if (sellingPlanId) restorePayload.selling_plan = sellingPlanId
        await GiftUtils.cartPost('/cart/add.js', restorePayload).catch(() => {})
        throw new Error(`/cart/add.js failed: ${addResp.status}`)
      }

      // 5) Re-add personalisation addon if it existed
      if (personalisationAddon) {
        GiftUtils.log('[removeGiftBox] Re-adding personalisation addon:', {
          variant_id: personalisationAddon.variant_id,
          properties: personalisationAddon.properties,
        })
        const personalisationResp = await GiftUtils.cartPost('/cart/add.js', {
          id: personalisationAddon.variant_id,
          quantity: personalisationAddon.quantity,
          properties: personalisationAddon.properties,
        })
        if (!personalisationResp.ok) {
          throw new Error('[Cart] Failed to re-add personalisation addon: ' + personalisationResp.status)
        }
      }

      const finalCart = await GiftUtils.fetchCart()
      GiftUtils.log('[removeGiftBox] Final cart', {
        items: finalCart.items.map((it, idx) => ({
          line: idx + 1,
          id: it.id,
          variant_id: it.variant_id,
          properties: it.properties,
        })),
      })
      publish(PUB_SUB_EVENTS.cartUpdate, {
        source: 'cart-items',
        cartData: finalCart,
      })
    } catch (err) {
      console.error('[Cart] Error removing gift box:', err)
      const errorsEl =
        document.getElementById('cart-errors') ||
        document.getElementById('CartDrawer-CartErrors')
      if (errorsEl) errorsEl.textContent = window.cartStrings.error
    } finally {
      this.disableLoading()

      // Always re-render to sync DOM with actual cart state
      this.onCartUpdate()
      if (window.updateCartItemCount) {
        try {
          await window.updateCartItemCount()
        } catch {}
      }
    }
  }

  async removePersonalisation(groupId) {
    try {
      this.enableLoading()

      const cart = await GiftUtils.fetchCart()
      GiftUtils.log('[removePersonalisation] Initial cart', {
        items: cart.items.map((it, idx) => ({
          line: idx + 1,
          id: it.id,
          variant_id: it.variant_id,
          properties: it.properties,
        })),
      })

      // 1) Snapshot all group items BEFORE any removals
      const mainItem = cart.items.find(
        item =>
          String(item.properties?._groupId) === String(groupId) &&
          !item.properties?._addon
      )
      if (!mainItem) throw new Error('Main item not found for groupId: ' + groupId)

      const hasGiftBox = Boolean(
        mainItem.properties?._hasGiftBox === 'true' ||
        mainItem.properties?._hasGiftBox === true ||
        mainItem.properties?._giftPid
      )

      const giftBoxAddon = hasGiftBox
        ? cart.items.find(
            item =>
              String(item.properties?._groupId) === String(groupId) &&
              (item.properties?._addon === 'true' || item.properties?._addon === true) &&
              item.properties?._addonType === 'Gift Box'
          )
        : null

      GiftUtils.log('[removePersonalisation] hasGiftBox:', hasGiftBox, 'giftBoxAddon:', giftBoxAddon)

      // 2) Validate personalisationStrings BEFORE any destructive operations
      // Property names come from the locale via window.personalisationStrings
      const initialsProperty = window.personalisationStrings?.initialsProperty
      const fontProperty = window.personalisationStrings?.fontProperty
      const colorProperty = window.personalisationStrings?.colorProperty

      if (!initialsProperty || !fontProperty || !colorProperty) {
        throw new Error('Missing personalisationStrings — cannot strip personalisation properties')
      }

      // 3) Remove ALL items in this group (descending line order to preserve indices)
      const groupLines = []
      cart.items.forEach((item, idx) => {
        if (String(item.properties?._groupId) === String(groupId)) {
          groupLines.push(idx + 1)
        }
      })
      groupLines.sort((a, b) => b - a)

      GiftUtils.log('[removePersonalisation] Removing all group lines:', groupLines)
      for (const line of groupLines) {
        const resp = await GiftUtils.cartPost('/cart/change.js', { line, quantity: 0 })
        if (!resp.ok) {
          throw new Error(`/cart/change.js failed removing line ${line}: ${resp.status}`)
        }
      }

      // 4) Build cleaned properties (remove personalisation props, keep gift box props)
      const cleanedProps = Object.entries(mainItem.properties || {}).reduce(
        (acc, [key, val]) => {
          if (key === initialsProperty || key === fontProperty || key === colorProperty) {
            return acc
          }
          if (key === '_groupId') {
            if (hasGiftBox) acc[key] = val
            return acc
          }
          if (val != null && val !== '') {
            acc[key] = val
          }
          return acc
        },
        {}
      )

      GiftUtils.log('[removePersonalisation] cleanedProps:', cleanedProps)

      // 4) Re-add main item with cleaned properties
      const addPayload = {
        id: mainItem.variant_id,
        quantity: mainItem.quantity,
        properties: cleanedProps,
      }
      const sellingPlanId = mainItem.selling_plan_allocation?.selling_plan?.id
      if (sellingPlanId) addPayload.selling_plan = sellingPlanId

      const addResp = await GiftUtils.cartPost('/cart/add.js', addPayload)
      if (!addResp.ok) {
        console.error('[Cart] Re-add main item failed, attempting restore')
        const restorePayload = {
          id: mainItem.variant_id,
          quantity: mainItem.quantity,
          properties: mainItem.properties,
        }
        if (sellingPlanId) restorePayload.selling_plan = sellingPlanId
        await GiftUtils.cartPost('/cart/add.js', restorePayload).catch(() => {})
        throw new Error(`/cart/add.js failed: ${addResp.status}`)
      }

      // 5) Re-add gift box addon if it existed
      if (giftBoxAddon) {
        GiftUtils.log('[removePersonalisation] Re-adding gift box addon:', {
          variant_id: giftBoxAddon.variant_id,
          properties: giftBoxAddon.properties,
        })
        const giftBoxResp = await GiftUtils.cartPost('/cart/add.js', {
          id: giftBoxAddon.variant_id,
          quantity: giftBoxAddon.quantity,
          properties: giftBoxAddon.properties,
        })
        if (!giftBoxResp.ok) {
          throw new Error('[Cart] Failed to re-add gift box addon: ' + giftBoxResp.status)
        }
      }

      const finalCart = await GiftUtils.fetchCart()
      GiftUtils.log('[removePersonalisation] Final cart', {
        items: finalCart.items.map((it, idx) => ({
          line: idx + 1,
          id: it.id,
          variant_id: it.variant_id,
          properties: it.properties,
        })),
      })
      publish(PUB_SUB_EVENTS.cartUpdate, {
        source: 'cart-items',
        cartData: finalCart,
      })
    } catch (err) {
      console.error('[Cart] Error removing personalisation:', err)
      const errorsEl =
        document.getElementById('cart-errors') ||
        document.getElementById('CartDrawer-CartErrors')
      if (errorsEl) {
        errorsEl.textContent = window.cartStrings.error
      }
    } finally {
      this.disableLoading()

      // Always re-render to sync DOM with actual cart state
      this.onCartUpdate()
      if (window.updateCartItemCount) {
        try {
          await window.updateCartItemCount()
        } catch {}
      }
    }
  }

  disconnectedCallback() {
    if (this.cartUpdateUnsubscriber) {
      this.cartUpdateUnsubscriber()
    }
    if (this.pageNavigationCleanup) {
      this.pageNavigationCleanup()
    }
  }

  onChange(event) {
    const parent = event.target.closest('.cart-item')
    const hasGiftBox = parent.dataset.productHasGiftbox === 'true'
    const hasMonogramProduct = parent.dataset.isPersonalised === 'true'

    let groupId

    if (hasGiftBox || hasMonogramProduct) {
      groupId = parent.dataset.groupId
    }

    this.updateQuantity(
      event.target.dataset.index,
      event.target.value,
      document.activeElement.getAttribute('name'),
      event.target.dataset.quantityVariantId,
      groupId
    )
  }

  onCartUpdate() {
    // Prevent multiple simultaneous updates
    if (this.isUpdating) {
      return
    }
    this.isUpdating = true

    const sections = this.getSectionsToRender()
      .map(section => section.section)
      .join(',')
    // Add cache-busting to prevent stale cart data when returning from checkout
    const cacheBuster = `&_=${Date.now()}`
    fetch(`${routes.cart_url}?sections=${sections}${cacheBuster}`, {
      cache: 'no-store',
      headers: {
        'X-Requested-With': 'XMLHttpRequest',
      },
    })
      .then(response => {
        if (!response.ok) {
          this.isUpdating = false
          throw new Error(`Cart update failed: ${response.status}`)
        }
        return response.text()
      })
      .then(state => {
        let parsedState = {}
        try {
          parsedState = JSON.parse(state)
        } catch (_) {
          this.isUpdating = false
          return
        }
        this.getSectionsToRender().forEach(section => {
          const elementToReplace =
            document
              .getElementById(section.id)
              ?.querySelector(section.selector) ||
            document.getElementById(section.id)
          if (!elementToReplace) return
          elementToReplace.innerHTML = GiftUtils.parseSectionHTML(
            parsedState[section.section],
            section.selector
          )
        })

        // Update cart count after sections are updated
        if (window.updateCartItemCount) {
          window.updateCartItemCount()
        }
      })
      .catch(e => {
        console.error(e)
      })
      .finally(() => {
        // Always clear the updating flag
        this.isUpdating = false

        // Ensure any loading states are cleared
        this.disableLoading()
      })
  }

  getSectionsToRender() {
    return [
      {
        id: 'main-cart-items',
        section: document.getElementById('main-cart-items').dataset.id,
        selector: '.js-contents',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
      {
        id: 'cart-live-region-text',
        section: 'cart-live-region-text',
        selector: '.shopify-section',
      },
      {
        id: 'main-cart-footer',
        section: document.getElementById('main-cart-footer').dataset.id,
        selector: '.js-contents',
      },
    ]
  }

  updateQuantity(line, quantity, name, variantId, groupId) {
    this.enableLoading()

    const body = JSON.stringify({
      line,
      quantity,
      sections: this.getSectionsToRender().map(section => section.section),
      sections_url: window.location.pathname,
    })

    fetch(`${routes.cart_change_url}`, { ...fetchConfig(), ...{ body } })
      .then(response => {
        return response.text()
      })
      .then(state => {
        const parsedState = JSON.parse(state)
        const quantityElement =
          document.getElementById(`Quantity-${line}`) ||
          document.getElementById(`Drawer-quantity-${line}`)
        const items = document.querySelectorAll('.cart-item')

        if (parsedState.errors) {
          quantityElement.value = quantityElement.getAttribute('value')
          this.updateLiveRegions(line, parsedState.errors)
          return
        }

        this.classList.toggle('is-empty', parsedState.item_count === 0)
        const cartDrawerWrapper = document.querySelector('cart-drawer')
        const cartFooter = document.getElementById('main-cart-footer')

        if (cartFooter)
          cartFooter.classList.toggle('is-empty', parsedState.item_count === 0)
        if (cartDrawerWrapper)
          cartDrawerWrapper.classList.toggle(
            'is-empty',
            parsedState.item_count === 0
          )

        if (!groupId) {
          this.getSectionsToRender().forEach(section => {
            const elementToReplace =
              document
                .getElementById(section.id)
                .querySelector(section.selector) ||
              document.getElementById(section.id)
            elementToReplace.innerHTML = GiftUtils.parseSectionHTML(
              parsedState.sections[section.section],
              section.selector
            )
          })
        }

        const updatedValue = parsedState.items[line - 1]
          ? parsedState.items[line - 1].quantity
          : undefined

        let message = ''
        if (
          items.length === parsedState.items.length &&
          updatedValue !== parseInt(quantityElement.value)
        ) {
          if (typeof updatedValue === 'undefined') {
            message = window.cartStrings.error
          } else {
            message = window.cartStrings.quantityError.replace(
              '[quantity]',
              updatedValue
            )
          }
        }
        this.updateLiveRegions(line, message)

        const lineItem =
          document.getElementById(`CartItem-${line}`) ||
          document.getElementById(`CartDrawer-Item-${line}`)
        if (lineItem && lineItem.querySelector(`[name="${name}"]`)) {
          cartDrawerWrapper
            ? trapFocus(
                cartDrawerWrapper,
                lineItem.querySelector(`[name="${name}"]`)
              )
            : lineItem.querySelector(`[name="${name}"]`).focus()
        } else if (parsedState.item_count === 0 && cartDrawerWrapper) {
          trapFocus(
            cartDrawerWrapper.querySelector('.drawer__inner-empty'),
            cartDrawerWrapper.querySelector('a')
          )
        } else if (document.querySelector('.cart-item') && cartDrawerWrapper) {
          trapFocus(
            cartDrawerWrapper,
            document.querySelector('.cart-item__name')
          )
        }
        publish(PUB_SUB_EVENTS.cartUpdate, {
          source: 'cart-items',
          cartData: parsedState,
          variantId: variantId,
        })

        /**
         * If a Group ID has been passed, find the addon product's cart index so
         * that that product can have it's quantity updated too.
         * - Gets the addon products with the same group ID.
         * - If there is only one addon, remove it.
         * - If there are multiple addons, call `updateQuantity` passing the
         *   `groupId` again to re-check for any remaining addon products to
         *   update.
         */
        if (groupId) {
          const linesToUpdate = this.findAddonProducts(parsedState, groupId)

          const quantityInputs = document.querySelectorAll(
            `[data-addon=true][data-group-id="${groupId}"] .quantity__input`
          )

          if (linesToUpdate.length === 1) {
            this.updateQuantity(linesToUpdate[0], quantity)
          } else if (linesToUpdate.length > 1) {
            this.updateQuantity(linesToUpdate[0], quantity, null, null, groupId)
          }

          quantityInputs.forEach(quantityInput => {
            quantityInput.value = quantity
          })
        }
      })
      .catch(() => {
        this.disableLoading()
        const errors =
          document.getElementById('cart-errors') ||
          document.getElementById('CartDrawer-CartErrors')
        errors.textContent = window.cartStrings.error
      })
      .finally(() => {
        setTimeout(() => {
          this.disableLoading()
        }, 1000)
      })
  }

  /**
   * Remove all cart lines (and any hidden/UI‐only rows) whose properties._groupId matches `groupId`.
   * Uses /cart/change.js one‐by‐one and calls onCartUpdate() after each removal, so the drawer always stays in sync.
   *
   * @param {string|number} groupId  The group ID to match against item.properties._groupId
   */
  async removeGroupItems(groupId) {
    this.enableLoading()

    try {
      // 2) Fetch /cart.js to see which lines (1-based) share this groupId
      const cart = await GiftUtils.fetchCart()

      // 3) Build an array of 1-based lines to delete (where _groupId matches)
      let linesToRemove = []
      cart.items.forEach((item, idx) => {
        if (
          item.properties &&
          String(item.properties._groupId) === String(groupId)
        ) {
          linesToRemove.push(idx + 1)
        }
      })

      // 4) If nothing matches, exit early (finally block handles disableLoading)
      if (linesToRemove.length === 0) {
        return
      }

      // 5) Sort descending so removing a higher line first doesn't shift indexes for the next lines
      linesToRemove.sort((a, b) => b - a)

      // 6) Loop through each line, remove it via /cart/change.js, then call onCartUpdate()
      for (const lineNum of linesToRemove) {
        const changeResp = await GiftUtils.cartPost('/cart/change.js', { line: lineNum, quantity: 0 })

        if (!changeResp.ok) {
          let errJson = {}
          try {
            errJson = await changeResp.json()
          } catch {}
          throw new Error(
            `/cart/change.js failed for line ${lineNum}: ${changeResp.status} – ${JSON.stringify(errJson)}`
          )
        }

        const updatedCart = await changeResp.json()

        // 7) Publish cartUpdate so mini‐cart badges, etc. refresh immediately
        publish(PUB_SUB_EVENTS.cartUpdate, {
          source: 'cart-items',
          cartData: updatedCart,
        })

        // 8) Call onCartUpdate() so your drawer (or main‐cart page) replaces itself with the latest HTML
        //    This covers both scenarios: if user is in a drawer, it re-fetches `?section_id=cart-drawer`;
        //    if on the main-cart page, it re-fetches `?section_id=main-cart-items`.
        this.onCartUpdate()

        // 9) Short pause to let the drawer re-render before removing the next line (optional, but smoother)
        await new Promise(r => setTimeout(r, 150))
      }

      // 11) After removing all lines for that group, we may still have “hidden/UI-only” rows in the DOM.
      //     Purge them now: any <.cart-item data-group-id="…"> left over (they never existed in /cart.js).
      document
        .querySelectorAll(`.cart-item[data-group-id="${groupId}"]`)
        .forEach(el => el.remove())

      // 12) Finally, fetch the cart one more time to know if it's empty (so we can trap focus appropriately).
      const finalCart = await GiftUtils.fetchCart()

      // 13) If the drawer still exists, toggle its “is-empty” class; otherwise skip
      const drawer = document.querySelector('cart-drawer')
      if (drawer) {
        drawer.classList.toggle('is-empty', finalCart.item_count === 0)
      }

      // 14) If the main‐cart‐footer exists, toggle its “is-empty” too
      const footer = document.getElementById('main-cart-footer')
      if (footer) {
        footer.classList.toggle('is-empty', finalCart.item_count === 0)
      }

      if (window.updateCartItemCount) {
        await window.updateCartItemCount()
      }

      // 15) Trap focus to the “empty” container if cart is now empty; otherwise to first item
      if (finalCart.item_count === 0 && drawer) {
        const emptyContainer = drawer.querySelector('.drawer__inner-empty')
        const fallbackLink = drawer.querySelector('a')
        if (emptyContainer && fallbackLink) {
          trapFocus(emptyContainer, fallbackLink)
        }
      } else if (document.querySelector('.cart-item') && drawer) {
        trapFocus(drawer, document.querySelector('.cart-item__name'))
      }

      // 16) Publish one more cartUpdate so any other parts of your theme (like the mini‐cart count badge)
      //     have the final state.
      publish(PUB_SUB_EVENTS.cartUpdate, {
        source: 'cart-items',
        cartData: finalCart,
      })
    } catch (err) {
      console.error('[Cart] Error removing group items:', err)
      const errorsEl =
        document.getElementById('cart-errors') ||
        document.getElementById('CartDrawer-CartErrors')
      if (errorsEl) {
        errorsEl.textContent = window.cartStrings.error
      }
      // Re-render on error to sync DOM with actual cart state
      this.onCartUpdate()
      if (window.updateCartItemCount) {
        try {
          await window.updateCartItemCount()
        } catch {}
      }
    } finally {
      this.disableLoading()
    }
  }

  /**
   * Find the line numbers for addon products with a matching group id.
   * - Does this by finding a product with `_addon: 'true'` and `_groupId`
   *   property that matches the passed `groupId`.
   * @param {Object} state - Cart state
   * @param {String|Number} groupId - Line item property value that groups main products
   * with their addon products.
   * @returns {Array<Number>}
   */
  findAddonProducts(state, groupId) {
    const addonLines = []
    state.items.forEach((item, index) => {
      if (
        item.properties &&
        item.properties._addon &&
        parseInt(item.properties._groupId) === parseInt(groupId)
      ) {
        const line = index + 1
        addonLines.push(line)
      }
    })

    return addonLines
  }

  updateLiveRegions(line, message) {
    const lineItemError =
      document.getElementById(`Line-item-error-${line}`) ||
      document.getElementById(`CartDrawer-LineItemError-${line}`)
    if (lineItemError)
      lineItemError.querySelector('.cart-item__error-text').innerHTML = message

    this.lineItemStatusElement.setAttribute('aria-hidden', true)

    const cartStatus =
      document.getElementById('cart-live-region-text') ||
      document.getElementById('CartDrawer-LiveRegionText')
    cartStatus.setAttribute('aria-hidden', false)

    setTimeout(() => {
      cartStatus.setAttribute('aria-hidden', true)
    }, 1000)
  }

  enableLoading() {
    GiftUtils.setCartLoading(true)
    document.activeElement.blur()
    this.lineItemStatusElement.setAttribute('aria-hidden', false)
  }

  disableLoading() {
    GiftUtils.setCartLoading(false)
  }

  initProgressBar() {
    this.fetchCartTotal()
      .then(cartTotal => {
        if (cartTotal > 0) {
          const formattedMoney = (cartTotal / 100).toFixed(2)
          this.updateProgressBar(formattedMoney)
        }
      })
      .catch(e => {
        console.error(e)
      })
  }

  fetchCartTotal() {
    return GiftUtils.fetchCart()
      .then(cart => {
        let cartTotal = 0

        cart.items.forEach(item => {
          if (!item.gift_card) {
            cartTotal += item.line_price
          }
        })
        return cartTotal
      })
  }

  updateProgressBar(cartTotal) {
    const progressBar = document.getElementById('shippingProgressBar')
    const cartInfo = document.getElementById('cart-info')
    const freeShippingThreshold = parseFloat(
      cartInfo?.dataset?.freeShippingThreshold
    )

    if (freeShippingThreshold) {
      const percentage = (cartTotal / freeShippingThreshold) * 100

      progressBar.style.width = `${percentage}%`
      progressBar.setAttribute('aria-valuenow', cartTotal)
      progressBar.setAttribute('aria-valuemax', freeShippingThreshold)
    }
  }
}

customElements.define('cart-items', CartItems)

if (!customElements.get('cart-note')) {
  customElements.define(
    'cart-note',
    class CartNote extends HTMLElement {
      constructor() {
        super()

        this.addEventListener(
          'input',
          debounce(event => {
            const body = JSON.stringify({ note: event.target.value })
            fetch(`${routes.cart_update_url}`, {
              ...fetchConfig(),
              ...{ body },
            })
          }, ON_CHANGE_DEBOUNCE_TIMER)
        )
      }
    }
  )
}
